import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiLoader, FiTrash2, FiFilm, FiInfo, FiPlus, FiX, FiZap, FiEdit3, FiUsers } from "react-icons/fi";
import { apiRequest, errorMessage } from "../api";
import { useJobHistory, formatSeconds } from "./audioShared";
import { LANGUAGES, RATIOS, inputClass, labelClass, card, Pills } from "./VideoStudioPanel";
import ToonStudio from "./toon/ToonStudio";
import CharacterThumb from "./toon/CharacterThumb";
import { KINDS, KIND_IDS, SETTINGS, SETTING_IDS, HAIR_STYLES, castMember, defaultLook } from "../video/toon/cast";
import { ACTION_IDS, EMOTIONS } from "../video/toon/rig";

const MAX_SCRIPT = 6000;
const LOOK_KEYS = ["skin", "hair", "hairStyle", "top", "bottom", "shoes", "fur", "accent"];
const LENGTHS = [["short", "Short (about 1 min)"], ["medium", "Medium (about 2 min)"], ["long", "Long (about 3-4 min)"]];
const TONES = [["fun", "Fun"], ["heartwarming", "Warm"], ["adventure", "Adventure"], ["funny", "Funny"], ["learning", "Teaches"]];
const IDEAS = ["The lion and the clever mouse", "A rabbit, a boy and a kind grandmother find a lost kitten", "Two friends and a dog go on a beach adventure", "Why we should keep our park clean"];
const SCRIPT_EXAMPLE = "Tom: (waves) Hi Mom! I am home.\nMom: (smiling) Welcome back, Tom. How was school?\nBuzz the Dog: (jumps) Woof woof! I missed you!\nTom: (laughs) I missed you too, Buzz!";
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const sentence = (text) => String(text || "").replace(/^\w/, (c) => c.toUpperCase());

// Colours and hair of a cast member as plain fields (so that they are saved with the screenplay and stay the same in the video).
const lookOf = (member, index) => {
  const full = castMember(member, index);
  return Object.fromEntries(LOOK_KEYS.filter((k) => full[k] !== undefined).map((k) => [k, full[k]]));
};
const withLooks = (play) => ({ ...play, cast: play.cast.map((m, i) => ({ ...m, ...lookOf(m, i) })) });

const Field = ({ label, children, htmlFor }) => (<div><label htmlFor={htmlFor} className="text-xs text-gray-400 block mb-1.5">{label}</label>{children}</div>);
const Swatch = ({ label, value, onChange }) => (
  <label className="flex items-center gap-1.5 text-[11px] text-gray-400"><input type="color" value={value || "#888888"} onChange={(e) => onChange(e.target.value)} aria-label={label} className="w-7 h-7 rounded border border-navy-border bg-transparent p-0.5" />{label}</label>
);

export default function ToonVideoTool() {
  const { clientId, history: everything, loading, elapsed, add, remove } = useJobHistory("video");
  const history = useMemo(() => everything.filter((job) => job.quality === "cartoon"), [everything]);
  const running = history.find((job) => job.status === "queued" || job.status === "processing");
  const latest = history[0];

  // the story
  const [title, setTitle] = useState("");
  const [script, setScript] = useState("");
  const [length, setLength] = useState("medium");
  const [tone, setTone] = useState("fun");
  const [language, setLanguage] = useState("en");
  const [style, setStyle] = useState("2d");
  const [ratio, setRatio] = useState("16:9");
  const [voices, setVoices] = useState([]);
  // the screenplay
  const [play, setPlay] = useState(null);                 // { title, cast, scenes, notice, engine }
  const [writing, setWriting] = useState(false);
  const [writeProgress, setWriteProgress] = useState("");
  const [writeError, setWriteError] = useState("");
  // the video
  const [submitting, setSubmitting] = useState(false);
  const [makeError, setMakeError] = useState("");
  const [viewId, setViewId] = useState(null);
  const [detail, setDetail] = useState(null);
  const resultRef = useRef(null);
  const screenplayRef = useRef(null);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    apiRequest("/core/tts/voices/").then(({ ok, data }) => { if (aliveRef.current && ok) setVoices(data.voices || []); });
    return () => { aliveRef.current = false; };
  }, []);
  const languages = useMemo(() => { const l = [...new Set(voices.map((v) => v.language))].filter((k) => LANGUAGES[k]); return l.length ? l : ["en"]; }, [voices]);
  const voicesHere = useMemo(() => voices.filter((v) => v.language === language), [voices, language]);

  useEffect(() => { if (latest && latest.status === "completed") setViewId(latest.id); }, [latest?.id, latest?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!viewId) { setDetail(null); return undefined; }
    let alive = true;
    apiRequest(`/core/voice-clone/${viewId}/?client_id=${encodeURIComponent(clientId)}`).then(({ ok, data }) => { if (alive) setDetail(ok ? data : null); });
    return () => { alive = false; };
  }, [viewId, clientId]);

  // ---------------------------------------------------------------------------------------- the screenplay
  const canWrite = (title.trim().length >= 3 || script.trim().length >= 20) && !writing;
  const writeScreenplay = async () => {
    setWriteError("");
    setWriteProgress("Sending...");
    setWriting(true);
    try {
      const form = new FormData();
      form.append("client_id", clientId);
      form.append("kind", "script");
      form.append("mode", "screenplay");
      form.append("title", title.trim());
      form.append("script", script.trim());
      form.append("length", length);
      form.append("tone", tone);
      form.append("language", language);
      form.append("style", style);
      form.append("ratio", ratio);
      const created = await apiRequest("/core/voice-clone/", { method: "POST", body: form });
      if (!created.ok) throw new Error(created.data?.title || errorMessage(created.data));
      let job = created.data;
      for (let n = 0; n < 150 && aliveRef.current && (job.status === "queued" || job.status === "processing"); n += 1) {
        setWriteProgress(job.progress || "Writing...");
        await sleep(2500);
        const polled = await apiRequest(`/core/voice-clone/${job.id}/?client_id=${encodeURIComponent(clientId)}`);
        if (polled.ok) job = polled.data;
      }
      if (!aliveRef.current) return;
      const result = job.segments && !Array.isArray(job.segments) ? job.segments : null;
      if (job.status !== "completed" || !result?.scenes) throw new Error(job.error_message || "The screenplay could not be written right now. Please try again in a minute.");
      setPlay(withLooks(result));
      setViewId(null);
      setTimeout(() => screenplayRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (e) {
      setWriteError(e.message || "The screenplay could not be written. Please try again.");
    }
    if (aliveRef.current) setWriting(false);
  };

  const patch = (change) => setPlay((p) => ({ ...p, ...change }));
  const editMember = (i, change) => setPlay((p) => ({ ...p, cast: p.cast.map((m, k) => (k === i ? { ...m, ...change } : m)) }));
  const changeKind = (i, kind) => editMember(i, { kind, voice: "", pitch: undefined, rate: undefined, ...defaultLook(kind, i), hairStyle: defaultLook(kind, i).hairStyle });
  const addMember = () => setPlay((p) => {
    if (p.cast.length >= 6) return p;
    const kind = "man";
    const used = new Set(p.cast.map((m) => m.name));
    let n = p.cast.length + 1;
    while (used.has(`Friend ${n}`)) n += 1;
    return { ...p, cast: [...p.cast, { id: `friend-${Date.now() % 100000}`, name: `Friend ${n}`, kind, voice: "", ...defaultLook(kind, p.cast.length) }] };
  });
  const removeMember = (i) => setPlay((p) => {
    const gone = p.cast[i].id;
    const cast = p.cast.filter((_, k) => k !== i);
    const scenes = p.scenes.map((s) => ({ ...s, beats: s.beats.filter((b) => b.speaker !== gone) })).filter((s) => s.beats.length);
    return { ...p, cast, scenes };
  });
  const editBeat = (si, bi, change) => setPlay((p) => ({ ...p, scenes: p.scenes.map((s, a) => (a !== si ? s : { ...s, beats: s.beats.map((b, c) => (c === bi ? { ...b, ...change } : b)) })) }));
  const setBeatAction = (si, bi, action) => setPlay((p) => ({ ...p, scenes: p.scenes.map((s, a) => (a !== si ? s : { ...s, beats: s.beats.map((b, c) => {
    if (c !== bi) return b;
    const actions = { ...(b.actions || {}) };
    delete actions[b.speaker];                                   // the choice of the visitor wins over what the writer's direction said
    return { ...b, action, actions };
  }) })) }));
  const removeBeat = (si, bi) => setPlay((p) => ({ ...p, scenes: p.scenes.map((s, a) => (a !== si ? s : { ...s, beats: s.beats.filter((_, c) => c !== bi) })).filter((s) => s.beats.length) }));
  const addBeat = (si) => setPlay((p) => ({ ...p, scenes: p.scenes.map((s, a) => (a !== si ? s : { ...s, beats: [...s.beats, { speaker: p.cast[0]?.id || null, text: "", action: "", emotion: "", moves: [], actions: {}, direction: "" }] })) }));
  const addScene = () => setPlay((p) => (p.scenes.length >= 8 ? p : { ...p, scenes: [...p.scenes, { setting: "park", time: "day", beats: [{ speaker: p.cast[0]?.id || null, text: "", action: "", emotion: "", moves: [], actions: {}, direction: "" }] }] }));
  const editScene = (si, change) => setPlay((p) => ({ ...p, scenes: p.scenes.map((s, a) => (a === si ? { ...s, ...change } : s)) }));
  const removeScene = (si) => setPlay((p) => ({ ...p, scenes: p.scenes.filter((_, a) => a !== si) }));

  const lines = play ? play.scenes.reduce((n, s) => n + s.beats.filter((b) => b.text.trim()).length, 0) : 0;
  const emptyLine = play && play.scenes.some((s) => s.beats.some((b) => !b.text.trim() && !(b.moves || []).length && !Object.keys(b.actions || {}).length && !b.action));
  const canMake = !!play && lines > 0 && play.cast.length > 0 && !submitting && !running;

  // ---------------------------------------------------------------------------------------- the video
  const makeVideo = async () => {
    setMakeError("");
    setSubmitting(true);
    const cleaned = { ...play, scenes: play.scenes.map((s) => ({ ...s, beats: s.beats.filter((b) => b.text.trim() || (b.moves || []).length || Object.keys(b.actions || {}).length || b.action) })).filter((s) => s.beats.length) };
    const form = new FormData();
    form.append("client_id", clientId);
    form.append("kind", "video");
    form.append("mode", "cartoon");
    form.append("title", (play.title || title).trim());
    form.append("language", language);
    form.append("style", style);
    form.append("ratio", ratio);
    form.append("screenplay", JSON.stringify({ title: play.title, cast: cleaned.cast, scenes: cleaned.scenes }));
    const { ok, data } = await apiRequest("/core/voice-clone/", { method: "POST", body: form });
    setSubmitting(false);
    if (!ok) { setMakeError(data?.title || data?.prompt || errorMessage(data)); return; }
    setViewId(null);
    add(data);
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const view = detail && detail.status === "completed" ? detail : null;
  const segments = view?.segments && !Array.isArray(view.segments) ? view.segments : {};
  const voiceName = (v) => `${v.name.split(" - ")[0]}${v.accent_label ? ` (${v.accent_label})` : ""}, ${v.gender}`;

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-14">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">Text to 2D &amp; 3D Video</h2>
      <p className="text-center text-gray-400 max-w-3xl mx-auto mb-9">
        Give a script - or just a title. The writer makes the screenplay with its characters, every man, woman, child and animal gets a voice of its own, and the cartoon is drawn
        in 2D or 3D: the characters walk, wave, jump and dance, their bodies move and their lips follow the words. Save it as an MP4.
      </p>

      <div className="grid lg:grid-cols-[1.25fr_1fr] gap-6 items-start">
        <div className={`${card} space-y-7`}>
          <div>
            <label htmlFor="toon-title" className={labelClass}>1. The title <span className="normal-case tracking-normal text-gray-500 font-normal">(enough on its own)</span></label>
            <input id="toon-title" className={inputClass} value={title} maxLength={150} dir="auto" onChange={(e) => setTitle(e.target.value)} placeholder="e.g. The lion and the clever mouse" />
            <div className="flex flex-wrap gap-2 mt-3">
              {IDEAS.map((idea) => <button key={idea} type="button" onClick={() => setTitle(idea)} className="px-3 py-1.5 rounded-full text-xs border border-navy-border text-gray-300 hover:border-accent-light bg-white/5">{idea}</button>)}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2.5">
              <label htmlFor="toon-script" className={`${labelClass} !mb-0`}>2. Your script <span className="normal-case tracking-normal text-gray-500 font-normal">(optional)</span></label>
              <button type="button" onClick={() => setScript(SCRIPT_EXAMPLE)} className="text-xs text-accent-light hover:underline">Show an example</button>
            </div>
            <textarea id="toon-script" className={`${inputClass} min-h-[9rem] leading-relaxed`} value={script} maxLength={MAX_SCRIPT} dir="auto" onChange={(e) => setScript(e.target.value)}
              placeholder={"Write it as lines of dialogue - Name: (what they do) what they say - and each name becomes a character.\nOr tell it as a story and the writer will turn it into scenes.\nLeave this empty to have the whole story written from the title."} />
            <p className="text-xs text-gray-500 mt-1.5">{script.length} / {MAX_SCRIPT}. Names such as Mom, Grandpa, Leo the lion or Mimi the mouse are recognised and given the right look and voice.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div><span className={labelClass}>3. Look</span><Pills label="Look" value={style} onChange={setStyle} options={[["2d", "Flat 2D cartoon"], ["3d", "Solid 3D cartoon"]]} /></div>
            <div><span className={labelClass}>Video shape</span><Pills label="Video shape" value={ratio} onChange={setRatio} options={RATIOS.map(([k, l]) => [k, l.replace("Landscape ", "").replace("Tall ", "").replace("Square ", "")])} /></div>
            <div><span className={labelClass}>Story length</span><Pills label="Story length" value={length} onChange={setLength} options={LENGTHS} /></div>
            <div><span className={labelClass}>Mood</span><Pills label="Mood" value={tone} onChange={setTone} options={TONES} /></div>
            <Field label="Language of the voices" htmlFor="toon-lang">
              <select id="toon-lang" className={inputClass} value={language} onChange={(e) => setLanguage(e.target.value)}>{languages.map((l) => <option key={l} value={l}>{LANGUAGES[l]}</option>)}</select>
            </Field>
          </div>
        </div>

        <div className="lg:sticky lg:top-24 space-y-4">
          <div className={card}>
            <span className={labelClass}><FiUsers className="inline -mt-0.5" size={12} /> Who can be in your cartoon</span>
            <div className="grid grid-cols-4 gap-2">
              {KIND_IDS.map((kind, i) => (
                <div key={kind} className="text-center">
                  <CharacterThumb member={{ kind }} index={i} size={72} />
                  <p className="text-[11px] text-gray-300 mt-1">{KINDS[kind].label}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-3">Men speak with a man&apos;s voice, women with a woman&apos;s, children with a child&apos;s. Animals get a lighter or a deeper voice to suit them (a lion is deep, a mouse is high).</p>
            <button type="button" onClick={writeScreenplay} disabled={!canWrite}
              className="w-full mt-5 flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-gradient text-white font-semibold shadow-lg shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed">
              {writing ? <><FiLoader className="animate-spin" /> {writeProgress || "Writing..."}</> : <><FiZap /> {play ? "Write the screenplay again" : "Write the screenplay"}</>}
            </button>
            {writeError && <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mt-3" role="alert">{writeError}</p>}
            {!canWrite && !writing && <p className="text-xs text-gray-500 text-center mt-2">Type a title or paste a script first</p>}
            <p className="text-xs text-gray-500 text-center mt-3">You can read and change the screenplay, the characters and their voices before the cartoon is made.</p>
          </div>
        </div>
      </div>

      {play && (
        <div ref={screenplayRef} className={`${card} mt-8 space-y-8`} data-testid="screenplay">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <span className={labelClass}><FiEdit3 className="inline -mt-0.5" size={12} /> 4. The screenplay <span className="normal-case tracking-normal text-gray-500 font-normal">- change anything you like</span></span>
              <input aria-label="Cartoon title" className={`${inputClass} font-semibold`} value={play.title} maxLength={150} dir="auto" onChange={(e) => patch({ title: e.target.value })} />
            </div>
            {play.engine && <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-gray-200">Written by {play.engine}</span>}
          </div>
          {play.notice && <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3">{play.notice}</p>}

          <div>
            <span className={labelClass}>Characters and voices</span>
            <div className="grid md:grid-cols-2 gap-4">
              {play.cast.map((m, i) => {
                const narrator = m.kind === "narrator";
                const info = KINDS[m.kind] || { ...KINDS.man, voice: "male" };
                const animal = info.species === "animal";
                const sameKind = voicesHere.filter((v) => v.gender === info.voice);
                return (
                  <div key={m.id} className="flex gap-4 rounded-xl border border-navy-border bg-navy/60 p-4">
                    <div className="shrink-0"><CharacterThumb member={m} index={i} size={84} /></div>
                    <div className="min-w-0 flex-1 space-y-2.5">
                      <div className="flex gap-2">
                        <input aria-label={`Character ${i + 1} name`} className={`${inputClass} !py-2`} value={m.name} maxLength={30} dir="auto" onChange={(e) => editMember(i, { name: e.target.value })} />
                        <button type="button" onClick={() => removeMember(i)} disabled={play.cast.length < 2} title="Remove this character and their lines" className="text-gray-400 hover:text-red-400 disabled:opacity-30 px-1"><FiTrash2 size={15} /></button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <select aria-label={`${m.name}: kind`} className={`${inputClass} !py-2`} value={m.kind} onChange={(e) => changeKind(i, e.target.value)}>
                          {KIND_IDS.map((k) => <option key={k} value={k}>{KINDS[k].label}</option>)}
                          <option value="narrator">Narrator (voice only)</option>
                        </select>
                        <select aria-label={`${m.name}: voice`} className={`${inputClass} !py-2`} value={m.voice || ""} onChange={(e) => editMember(i, { voice: e.target.value })}>
                          <option value="">Automatic ({info.voice} voice)</option>
                          {sameKind.map((v) => <option key={v.id} value={v.id}>{voiceName(v)}</option>)}
                          {voicesHere.filter((v) => v.gender !== info.voice).map((v) => <option key={v.id} value={v.id}>{voiceName(v)}</option>)}
                        </select>
                      </div>
                      {narrator ? <p className="text-xs text-gray-500">Only the voice is heard - the narrator is not drawn.</p> : (
                        <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                          {!animal && <Swatch label="Skin" value={m.skin} onChange={(v) => editMember(i, { skin: v })} />}
                          {!animal && <Swatch label="Hair" value={m.hair} onChange={(v) => editMember(i, { hair: v })} />}
                          {animal && <Swatch label="Fur" value={m.fur} onChange={(v) => editMember(i, { fur: v })} />}
                          <Swatch label="Top" value={m.top} onChange={(v) => editMember(i, { top: v })} />
                          {!animal && <Swatch label="Trousers" value={m.bottom} onChange={(v) => editMember(i, { bottom: v })} />}
                        </div>
                      )}
                      {!animal && !narrator && (HAIR_STYLES[m.kind] || []).length > 1 && (
                        <select aria-label={`${m.name}: hair style`} className={`${inputClass} !py-1.5 !text-xs`} value={m.hairStyle} onChange={(e) => editMember(i, { hairStyle: e.target.value })}>
                          {HAIR_STYLES[m.kind].map((h) => <option key={h} value={h}>Hair: {h}</option>)}
                        </select>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {play.cast.length < 6 && <button type="button" onClick={addMember} className="mt-3 text-xs text-accent-light hover:underline inline-flex items-center gap-1"><FiPlus size={13} /> Add a character</button>}
          </div>

          {play.scenes.map((scene, si) => (
            <div key={si} className="rounded-xl border border-navy-border bg-navy/40 p-4">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <span className="w-7 h-7 rounded-full bg-white/10 text-xs font-bold flex items-center justify-center">{si + 1}</span>
                <select aria-label={`Scene ${si + 1} place`} className={`${inputClass} !w-auto !py-2`} value={scene.setting} onChange={(e) => editScene(si, { setting: e.target.value })}>{SETTING_IDS.map((k) => <option key={k} value={k}>{SETTINGS[k].label}</option>)}</select>
                <select aria-label={`Scene ${si + 1} time of day`} className={`${inputClass} !w-auto !py-2`} value={scene.time} onChange={(e) => editScene(si, { time: e.target.value })}><option value="day">Day</option><option value="evening">Evening</option><option value="night">Night</option></select>
                {play.scenes.length > 1 && <button type="button" onClick={() => removeScene(si)} className="ms-auto text-xs text-gray-400 hover:text-red-400 inline-flex items-center gap-1"><FiX size={13} /> Remove scene</button>}
              </div>
              <ol className="space-y-2">
                {scene.beats.map((b, bi) => (
                  <li key={bi} className="flex flex-wrap items-start gap-2">
                    {b.speaker || b.text ? (
                      <>
                        <select aria-label={`Scene ${si + 1} line ${bi + 1} speaker`} className={`${inputClass} !w-32 !py-2`} value={b.speaker || ""} onChange={(e) => editBeat(si, bi, { speaker: e.target.value || null })}>
                          {play.cast.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </select>
                        <select aria-label={`Scene ${si + 1} line ${bi + 1} action`} className={`${inputClass} !w-28 !py-2`} value={(b.speaker && b.actions?.[b.speaker]) || b.action || ""} onChange={(e) => setBeatAction(si, bi, e.target.value)}>
                          <option value="">Talks</option>{ACTION_IDS.filter((a) => a !== "idle" && a !== "talk" && a !== "walk" && a !== "run").map((a) => <option key={a} value={a}>{sentence(a)}</option>)}
                        </select>
                        <select aria-label={`Scene ${si + 1} line ${bi + 1} feeling`} className={`${inputClass} !w-28 !py-2`} value={b.emotion || ""} onChange={(e) => editBeat(si, bi, { emotion: e.target.value })}>
                          <option value="">Feeling</option>{Object.entries(EMOTIONS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                        <input aria-label={`Scene ${si + 1} line ${bi + 1} words`} className={`${inputClass} !py-2 flex-1 min-w-[12rem]`} value={b.text} maxLength={260} dir="auto" placeholder="What they say" onChange={(e) => editBeat(si, bi, { text: e.target.value })} />
                      </>
                    ) : (
                      <p className="flex-1 text-xs text-gray-400 italic px-3 py-2 rounded-lg bg-white/5" dir="auto">{b.direction || "Movement"} <span className="not-italic text-gray-500">(no words)</span></p>
                    )}
                    <button type="button" onClick={() => removeBeat(si, bi)} aria-label={`Remove scene ${si + 1} line ${bi + 1}`} className="text-gray-500 hover:text-red-400 p-2"><FiX size={14} /></button>
                  </li>
                ))}
              </ol>
              <button type="button" onClick={() => addBeat(si)} className="mt-3 text-xs text-accent-light hover:underline inline-flex items-center gap-1"><FiPlus size={13} /> Add a line</button>
            </div>
          ))}
          {play.scenes.length < 8 && <button type="button" onClick={addScene} className="text-xs text-accent-light hover:underline inline-flex items-center gap-1"><FiPlus size={13} /> Add a scene</button>}

          <div className="border-t border-navy-border pt-6">
            <button type="button" onClick={makeVideo} disabled={!canMake}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-brand-gradient text-white font-semibold shadow-lg shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed">
              {submitting ? <><FiLoader className="animate-spin" /> Sending...</> : running ? <><FiLoader className="animate-spin" /> Recording the voices...</> : <><FiFilm /> Make the cartoon ({lines} {lines === 1 ? "line" : "lines"}, {play.cast.length} {play.cast.length === 1 ? "character" : "characters"})</>}
            </button>
            {emptyLine && <p className="text-xs text-gray-500 mt-2">Empty lines are left out.</p>}
            {makeError && <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mt-3" role="alert">{makeError}</p>}
            <p className="text-xs text-gray-500 mt-3">The voices are recorded in a few seconds. Then you can watch the cartoon, switch between the 2D and the 3D look, and save it as an MP4.</p>
          </div>
        </div>
      )}

      <div ref={resultRef} />
      {running && (
        <div className="rounded-2xl bg-white/5 border border-accent-light/30 p-6 mt-6 flex items-center gap-3">
          <FiLoader className="animate-spin text-accent-light shrink-0" size={22} />
          <div><p className="font-semibold">Recording the voices... {formatSeconds(elapsed)}{running.progress && <span className="text-gray-400 font-normal"> · {running.progress}</span>}</p><p className="text-sm text-gray-400">Every character reads their lines. You can leave this page open.</p></div>
        </div>
      )}
      {latest && latest.status === "failed" && !running && (
        <div className="rounded-2xl bg-red-500/10 border border-red-500/30 p-5 mt-6 text-sm text-red-200 flex items-start gap-3"><FiInfo className="mt-0.5 shrink-0" /><span>{latest.error_message || "The cartoon could not be made. Please try again."}</span></div>
      )}
      {view && !running && segments.source === "cartoon" && <ToonStudio key={view.id} view={view} segments={segments} mode={segments.style === "3d" ? "3d" : "2d"} />}

      <p className="text-xs text-gray-500 text-center mt-6">The characters are drawn in your browser, so the cartoon is yours to use. Do not use cartoons to mislead people or to make fun of real people.</p>

      <div className="mt-12">
        <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4">Your cartoons</p>
        {loading ? <p className="text-sm text-gray-400">Loading...</p> : history.length === 0 ? <p className="text-sm text-gray-500">Nothing here yet. Your cartoons will appear here.</p> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {history.map((job) => (
              <div key={job.id} className={`rounded-xl bg-white/5 border overflow-hidden ${viewId === job.id ? "border-accent-light/60" : "border-navy-border"}`}>
                {job.status === "completed" ? (
                  <button type="button" onClick={() => setViewId(job.id)} className="block w-full aspect-video bg-gradient-to-br from-[#16324f] to-[#0a1a2e] text-xs text-gray-300 font-semibold" aria-label={`Open ${job.original_name}`}>Watch</button>
                ) : (
                  <div className="w-full aspect-video flex items-center justify-center text-xs text-gray-500 px-3 text-center bg-navy">
                    {job.status === "failed" ? <span className="text-red-300">{job.error_message}</span> : <span className="inline-flex items-center gap-2"><FiLoader className="animate-spin" /> Working...</span>}
                  </div>
                )}
                <div className="p-3">
                  <p className="text-xs text-gray-200 line-clamp-2 min-h-[2rem]" dir="auto">{job.original_name}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[11px] text-gray-500">{new Date(job.created_at).toLocaleDateString()}</span>
                    {job.status !== "processing" && job.status !== "queued" && <button onClick={() => { if (viewId === job.id) setViewId(null); remove(job); }} title="Delete" className="text-gray-400 hover:text-red-400"><FiTrash2 size={14} /></button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
